import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { mealSubscriptionService } from "../services/mealSubscription.service";

// Fetch all meal subscriptions
export const fetchMealSubscriptions = createAsyncThunk(
  "mealSubscription/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await mealSubscriptionService.getMealSubscriptions();
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to fetch meal subscriptions"
      );
    }
  }
);

// Fetch active meal subscriptions
export const fetchActiveMealSubscriptions = createAsyncThunk(
  "mealSubscription/fetchActive",
  async (_, thunkAPI) => {
    try {
      return await mealSubscriptionService.getActiveMealSubscriptions();
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to fetch active meal subscriptions"
      );
    }
  }
);

// Fetch inactive meal subscriptions
export const fetchInactiveMealSubscriptions = createAsyncThunk(
  "mealSubscription/fetchInactive",
  async (_, thunkAPI) => {
    try {
      return await mealSubscriptionService.getInactiveMealSubscriptions();
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to fetch inactive meal subscriptions"
      );
    }
  }
);

// Create meal subscription
export const addMealSubscription = createAsyncThunk(
  "mealSubscription/add",
  async (subscription, thunkAPI) => {
    try {
      return await mealSubscriptionService.createMealSubscription(
        subscription
      );
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to create meal subscription"
      );
    }
  }
);

// Update meal subscription
export const editMealSubscription = createAsyncThunk(
  "mealSubscription/edit",
  async ({ id, subscription }, thunkAPI) => {
    try {
      await mealSubscriptionService.updateMealSubscription(
        id,
        subscription
      );

      return {
        mealSubscriptionId: id,
        ...subscription,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to update meal subscription"
      );
    }
  }
);

// Delete meal subscription
export const removeMealSubscription = createAsyncThunk(
  "mealSubscription/remove",
  async (id, thunkAPI) => {
    try {
      await mealSubscriptionService.deleteMealSubscription(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        "Failed to delete meal subscription"
      );
    }
  }
);

const mealSubscriptionSlice = createSlice({
  name: "mealSubscription",

  initialState: {
    subscriptions: [],
    activeSubscriptions: [],
    inactiveSubscriptions: [],

    loading: false,
    activeLoading: false,
    inactiveLoading: false,

    error: null,
    activeError: null,
    inactiveError: null,

    showModal: false,
  },

  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },

    closeModal: (state) => {
      state.showModal = false;
    },
  },

  extraReducers: (builder) => {
    builder

      // =====================================================
      // ALL SUBSCRIPTIONS
      // =====================================================

      .addCase(fetchMealSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchMealSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })

      .addCase(fetchMealSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =====================================================
      // ACTIVE SUBSCRIPTIONS
      // =====================================================

      .addCase(fetchActiveMealSubscriptions.pending, (state) => {
        state.activeLoading = true;
        state.activeError = null;
      })

      .addCase(fetchActiveMealSubscriptions.fulfilled, (state, action) => {
        state.activeLoading = false;
        state.activeSubscriptions = action.payload;
      })

      .addCase(fetchActiveMealSubscriptions.rejected, (state, action) => {
        state.activeLoading = false;
        state.activeError = action.payload;
      })

      // =====================================================
      // INACTIVE SUBSCRIPTIONS
      // =====================================================

      .addCase(fetchInactiveMealSubscriptions.pending, (state) => {
        state.inactiveLoading = true;
        state.inactiveError = null;
      })

      .addCase(
        fetchInactiveMealSubscriptions.fulfilled,
        (state, action) => {
          state.inactiveLoading = false;
          state.inactiveSubscriptions = action.payload;
        }
      )

      .addCase(
        fetchInactiveMealSubscriptions.rejected,
        (state, action) => {
          state.inactiveLoading = false;
          state.inactiveError = action.payload;
        }
      )

      // =====================================================
      // ADD
      // =====================================================

      .addCase(addMealSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(addMealSubscription.fulfilled, (state, action) => {
        state.subscriptions.push(action.payload);

        // If the newly created subscription is active,
        // also add it to activeSubscriptions.
        if (action.payload.subscriptionIsActive) {
          state.activeSubscriptions.push(action.payload);
        } else {
          state.inactiveSubscriptions.push(action.payload);
        }

        state.loading = false;
        state.showModal = false;
      })

      .addCase(addMealSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =====================================================
      // EDIT
      // =====================================================

      .addCase(editMealSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(editMealSubscription.fulfilled, (state, action) => {
        const editedSub = action.payload;

        // Update all subscriptions
        state.subscriptions = state.subscriptions.map((sub) =>
          sub.mealSubscriptionId === editedSub.mealSubscriptionId
            ? { ...sub, ...editedSub }
            : sub
        );

        // Remove it from both lists first
        state.activeSubscriptions =
          state.activeSubscriptions.filter(
            (sub) =>
              sub.mealSubscriptionId !==
              editedSub.mealSubscriptionId
          );

        state.inactiveSubscriptions =
          state.inactiveSubscriptions.filter(
            (sub) =>
              sub.mealSubscriptionId !==
              editedSub.mealSubscriptionId
          );

        // Put it into the correct list
        if (editedSub.subscriptionIsActive) {
          state.activeSubscriptions.push(editedSub);
        } else {
          state.inactiveSubscriptions.push(editedSub);
        }

        state.loading = false;
        state.showModal = false;
      })

      .addCase(editMealSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // =====================================================
      // DELETE
      // =====================================================

      .addCase(removeMealSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(removeMealSubscription.fulfilled, (state, action) => {
        const deletedId = action.payload;

        state.subscriptions =
          state.subscriptions.filter(
            (sub) => sub.mealSubscriptionId !== deletedId
          );

        state.activeSubscriptions =
          state.activeSubscriptions.filter(
            (sub) => sub.mealSubscriptionId !== deletedId
          );

        state.inactiveSubscriptions =
          state.inactiveSubscriptions.filter(
            (sub) => sub.mealSubscriptionId !== deletedId
          );

        state.loading = false;
      })

      .addCase(removeMealSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
} = mealSubscriptionSlice.actions;

export default mealSubscriptionSlice.reducer;